import { useEffect, useMemo, useState } from "react"

import { Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { BehaviorSubject } from "rxjs"
import { distinctUntilChanged, finalize, switchMap } from "rxjs/operators"

import PendingDrugTestRow from "./PendingDrugTestRow"
import OrphanedRow from "../../../api/OrphanedResults/Models/OrphanedRow"
import PendingDrugTest from "../../../api/OrphanedResults/Models/PendingDrugTest"
import PendingDrugTestGridData from "../../../api/OrphanedResults/Models/PendingDrugTestGridData"
import PendingDrugTestOptions from "../../../api/OrphanedResults/Models/PendingDrugTestOptions"
import { GetPendingDrugTestsObservable } from "../../../api/OrphanedResults/ResultExceptionsProcess"
import FormatError from "../../../formatters/FormatError"
import TablePagination from "../../TablePagination"

const PageSize = 5
interface PendingDrugTestsProps {
  donorId: number
  importTest: OrphanedRow
  selectedTest?: PendingDrugTest
  onSelectTest: (test: PendingDrugTest | undefined) => void
}

function PendingDrugTests(props: PendingDrugTestsProps) {
  const { donorId, importTest, selectedTest, onSelectTest } = props
  const [gridData, setGridData] = useState<PendingDrugTestGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loadingTests, setLoadingTests] = useState<boolean>(false)

  const page$ = useMemo(
    () =>
      new BehaviorSubject<PendingDrugTestOptions>({
        DonorID: donorId,
        PageNumber: 1,
        PageSize,
      }),
    [donorId]
  )

  useEffect(() => {
    setLoadingTests(true)
    const subscription = page$
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => GetPendingDrugTestsObservable(params).pipe(finalize(() => setLoadingTests(false))))
      )
      .subscribe(
        (data) => {
          const parsedRecords = data.Records.map((test) => ({
            ...test,
            DateOrdered: test.DateOrdered ? new Date(test.DateOrdered) : undefined,
            ProgramStartDate: test.ProgramStartDate ? new Date(test.ProgramStartDate) : undefined,
            ProgramEndDate: test.ProgramEndDate ? new Date(test.ProgramEndDate) : undefined,
          }))
          setGridData({ ...data, Records: parsedRecords })
        },
        (error) => toast.error(FormatError(error))
      )
    return () => subscription.unsubscribe()
  }, [page$])

  const handleSelectTest = (testId: string | undefined) => {
    const selected = gridData.Records.find((t) => t.TestDetailID === testId)
    onSelectTest(selected?.TestDetailID === selectedTest?.TestDetailID ? undefined : selected)
  }

  const handleChangePage = (page: number) => {
    handleSelectTest(undefined)
    page$.next({ ...page$.value, PageNumber: page })
  }

  return (
    <>
      <h5 className="text-body">Pending Drug Tests</h5>
      <table className="table table-bordered">
        <thead className="thead-light">
          <tr>
            <th className="text-center col-1" aria-label="..." />
            <th className="text-nowrap">Reason</th>
            <th className="text-nowrap col-2">PoolID</th>
            <th className="text-nowrap col-2">Regulated</th>
            <th className="text-nowrap">Type</th>
            <th className="text-nowrap col-2">Created</th>
          </tr>
        </thead>
        <tbody className="bg-light">
          {loadingTests && (
            <tr>
              <td colSpan={6}>
                <Spinner animation="border" />
              </td>
            </tr>
          )}
          {!loadingTests && gridData.Records.length === 0 && (
            <tr>
              <td colSpan={6}>Not Tests Found</td>
            </tr>
          )}
          {!loadingTests &&
            gridData.Records.map((test: PendingDrugTest) => (
              <PendingDrugTestRow
                key={`pdt_${test.TestDetailID}`}
                test={test}
                highlighted={importTest.Reason === test.ReasonDescription}
                hideCheckbox={test.ReasonDescription === "Random" && importTest.Reason !== test.ReasonDescription}
                selected={selectedTest?.TestDetailID === test.TestDetailID}
                onSelect={handleSelectTest}
              />
            ))}
        </tbody>
      </table>
      {!loadingTests && gridData.Records.length > 0 && (
        <>
          <span className="total-records">Total records: {gridData.Total}</span>
          <TablePagination pageNumber={page$.value.PageNumber} pages={gridData.Pages} onChange={handleChangePage} />
        </>
      )}
    </>
  )
}

PendingDrugTests.defaultProps = {
  selectedTest: undefined,
}

export default PendingDrugTests
